import React, { useState } from "react";

const PaymentButton = ({ setIsPaid, amount, validateForm, promoCode }) => {
  const [loading, setLoading] = useState(false);

  const isLegacyAndroid = () => {
    const ua = navigator.userAgent || "";
    return /Android [0-6]\./.test(ua);
  };

  const isInAppBrowser = () => {
    const ua = navigator.userAgent.toLowerCase();
    return (
      /fbav|instagram|line|wv|snapchat|linkedin|okhttp/.test(ua) ||
      (ua.includes("mobile") && ua.includes("version/") && !ua.includes("chrome"))
    );
  };

  const loadRazorpayScript = () => {
    return new Promise((resolve, reject) => {
      if (window.Razorpay) {
        resolve(true);
        return;
      }

      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.async = true;

      script.onload = () => {
        console.log("✅ Razorpay script loaded");
        resolve(true);
      };

      script.onerror = () => {
        console.error("❌ Failed to load Razorpay SDK");
        reject("Failed to load Razorpay SDK");
      };

      document.body.appendChild(script);
    });
  };

  const handlePayment = async () => {
    if (!validateForm()) return;

    if (isLegacyAndroid()) {
      alert(
        "⚠️ Your device or browser may not support the payment gateway. Please update your browser or try on a newer device."
      );
      return;
    }

    if (isInAppBrowser()) {
      alert(
        "⚠️ Payments may not work in this browser (e.g., Instagram/Facebook). Please open in Chrome or your system browser for a smooth payment experience."
      );
      return;
    }

    setLoading(true);

    try {
      await loadRazorpayScript();

      const response = await fetch("https://pitics4.onrender.com/create-order", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({ amount, promoCode }),
      });

      const data = await response.json();

      if (!data.success) {
        alert(data.error || "Failed to create order. Try again.");
        setLoading(false);
        return;
      }

      const options = {
        key: "rzp_live_eYF8RXUJ9ySFMa",
        amount: data.amount,
        currency: data.currency,
        name: "Pitics Technologies",
        description: "Payment for PDF Download",
        image: "https://pitics.com/logo.png",
        order_id: data.orderId,
        prefill: {
          name: "",
          email: "",
          contact: "",
        },
        theme: { color: "#3399cc" },
        handler: async function (response) {
          console.log("✅ Payment success:", response);

          try {
            const verifyResponse = await fetch("https://pitics4.onrender.com/verify-payment", {
              method: "POST",
              headers: {
                "Content-Type": "application/json",
              },
              credentials: "include",
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature,
              }),
            });

            const result = await verifyResponse.json();

            if (result.success) {
              setIsPaid(true);
              alert("✅ Payment verified! You can now generate the PDF.");
            } else {
              // ⬇️ Try local fallback if backend verification failed
              const localVerified = await verifyRazorpayPaymentFallback(
                response.razorpay_order_id,
                response.razorpay_payment_id,
                response.razorpay_signature
              );

              if (localVerified) {
                setIsPaid(true);
                alert("✅ Payment verified locally (fallback). You can now generate the PDF.");
              } else {
                alert("❌ Payment verification failed. Please contact support.");
              }
            }
          } catch (err) {
            console.error("❌ Backend verification failed. Trying fallback...");
            const localVerified = await verifyRazorpayPaymentFallback(
              response.razorpay_order_id,
              response.razorpay_payment_id,
              response.razorpay_signature
            );

            if (localVerified) {
              setIsPaid(true);
              alert("✅ Payment verified locally (fallback). You can now generate the PDF.");
            } else {
              alert("❌ Payment verification failed. Please contact support.");
            }
          }
        },
        modal: {
          ondismiss: function () {
            // Optional
          },
        },
        method: {
          netbanking: true,
          card: true,
          upi: true,
          wallet: true,
        },
        redirect: true,
        config: {
          display: {
            blocks: {
              all_methods: {
                name: "4G📱 Users prefer Card Payments (⚠️UPI failures️)",
                instruments: [
                  { method: "card" },
                  { method: "upi" },
                  { method: "wallet" },
                  { method: "paylater" },
                  { method: "netbanking" },
                ],
              },
            },
            sequence: ["block.all_methods"],
            preferences: {
              show_default_blocks: false,
            },
          },
        },
      };

      const rzp = new window.Razorpay(options);

      setTimeout(() => {
        rzp.open();
      }, 100);
    } catch (error) {
      console.error("❌ Payment error:", error);
      alert("❌ Payment failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Fallback verification using Web Crypto API (insecure alternative, but illustrative)
  const verifyRazorpayPaymentFallback = async (
    razorpay_order_id,
    razorpay_payment_id,
    razorpay_signature
  ) => {
    try {
      const encoder = new TextEncoder();
      const data = `${razorpay_order_id}|${razorpay_payment_id}`;

      const key = await window.crypto.subtle.importKey(
        "raw",
        new TextEncoder().encode("RAZORPAY_SECRET_REPLACE_ME"), // Replace with actual secret if unavoidable
        { name: "HMAC", hash: "SHA-256" },
        false,
        ["sign"]
      );

      const signatureBuffer = await window.crypto.subtle.sign(
        "HMAC",
        key,
        encoder.encode(data)
      );

      const generatedSignature = Array.from(new Uint8Array(signatureBuffer))
        .map((b) => b.toString(16).padStart(2, "0"))
        .join("");

      return generatedSignature === razorpay_signature;
    } catch (err) {
      console.error("❌ Fallback verification failed:", err);
      return false;
    }
  };

  return (
    <button
      type="button"
      onClick={handlePayment}
      disabled={loading}
      style={{
        padding: "10px 20px",
        fontSize: "16px",
        backgroundColor: loading ? "#6c757d" : "#28a745",
        color: "white",
        border: "none",
        borderRadius: "5px",
        cursor: loading ? "not-allowed" : "pointer",
        opacity: 1,
      }}
    >
      {loading ? "Processing..." : `Pay ₹${amount / 100}`}
    </button>
  );
};

export default PaymentButton;
